/**
 * Back In Stock Notification Form
 * Custom element for handling out-of-stock product notifications
 */

/* global fbq, subscribe */

if (!customElements.get('bis-form')) {
  // How long to wait for Shopify's contact response (covers an hCaptcha puzzle) before showing an error
  const BIS_SUBMIT_TIMEOUT_MS = 120000;
  const BIS_NAMED_ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: '\'' };

  /**
   * Decode HTML entities in a translation string. Shopify's `t` filter HTML-escapes
   * ("We'll" -> "We&#39;ll") and `json` then escapes the "&" as \u0026, so the browser
   * never decodes the entity and JSON.parse returns it literally. One pass only, and the
   * result is still only ever written with textContent (never innerHTML).
   * @param {String} value
   * @returns {String}
   */
  function decodeEntities(value) {
    if (typeof value !== 'string') return value;
    return value.replace(/&(#\d+|#x[0-9a-f]+|amp|lt|gt|quot|apos);/gi, (match, entity) => {
      const key = entity.toLowerCase();
      if (key.charAt(0) === '#') {
        const code = key.charAt(1) === 'x' ? parseInt(key.slice(2), 16) : parseInt(key.slice(1), 10);
        return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
      }
      return BIS_NAMED_ENTITIES[key];
    });
  }

  class BISForm extends HTMLElement {
    constructor() {
      super();
      this.debounceTimer = null;
      this.isSubmitting = false;
      this.initialized = false;
    }

    loadTranslations() {
      const dataAttr = this.getAttribute('data-translations');
      let translations = {};

      if (dataAttr) {
        try {
          translations = JSON.parse(dataAttr);
          Object.keys(translations).forEach((key) => {
            translations[key] = decodeEntities(translations[key]);
          });
        } catch (e) {
          console.error('Failed to parse translations:', e);
        }
      }

      return {
        emailRequired: translations.emailRequired || 'We\'ll need your email to notify you',
        nameRequired: translations.nameRequired || 'Please enter your first name',
        emailInvalid: translations.emailInvalid || 'Please enter a valid email address',
        phoneInvalid: translations.phoneInvalid || 'Please enter a valid phone number',
        nameInvalid: translations.nameInvalid || 'Please enter a valid name',
        phoneTooShort: translations.phoneTooShort || 'Phone number seems too short',
        tooShort: translations.tooShort || 'Must be at least {min_length} characters',
        validationFailed: translations.validationFailed || 'Please check the form and try again',
        submissionFailed: translations.submissionFailed || 'Something went wrong. Please try again.',
        submitting: translations.submitting || 'Sending...',
        submitButtonDefault: translations.submitButtonDefault || 'Notify Me',
        successNotification: translations.successNotification || 'You\'re on the list!'
      };
    }

    setupEventListeners() {
      this.triggerButton.addEventListener('click', this.openModal.bind(this));
      this.closeButton.addEventListener('click', this.closeModal.bind(this));
      this.cancelButton.addEventListener('click', this.closeModal.bind(this));
      this.overlay.addEventListener('click', this.closeModal.bind(this));
      this.form.addEventListener('submit', this.handleSubmit.bind(this));

      this.handleEscape = (e) => {
        if (e.key === 'Escape' && !this.modal.hidden) {
          this.closeModal();
        }
      };
      document.addEventListener('keydown', this.handleEscape);

      const inputs = this.form.querySelectorAll('input[required]');
      inputs.forEach(input => {
        input.addEventListener('input', this.debounceValidation.bind(this));
        input.addEventListener('blur', () => this.validateField(input));
      });
    }

    setupVariantChangeListener() {
      // Subscribe to theme's PUB_SUB variant change events
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        this.unsubscribeVariant = subscribe(window.PUB_SUB_EVENTS.variantChange, (data) => {
          if (data && data.variant) {
            this.updateVariantData(data.variant);
          }
        });
      }
    }

    updateVariantData(variant) {
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
      }
      if (this.productSkuInput) {
        this.productSkuInput.value = variant.sku || '';
      }

      // Update price field
      const priceInput = this.form.querySelector('input[name="contact[product_price]"]');
      if (priceInput && variant.price) {
        priceInput.value = variant.price;
      }

      if (this.bodyInput) {
        const variantTitle = variant.title || '';
        const messageTemplate = this.bodyInput.dataset.template || '';
        if (messageTemplate) {
          this.bodyInput.value = messageTemplate.replace('__VARIANT_TITLE__', () => variantTitle);
        }
        this.bodyInput.dataset.variantTitle = variantTitle;
      }

      // Toggle wrapper and buttons based on variant availability
      const isAvailable = this.getVariantAvailability(variant);
      this.toggleWrapper(!isAvailable); // Show wrapper when OUT of stock
      this.toggleAddToCart(isAvailable); // Show add to cart when IN stock
    }

    getVariantAvailability(variant) {
      // Priority 1: Use variant data directly if available
      if (variant && variant.available !== undefined) {
        return variant.available;
      }

      // Priority 2: Query product JSON data from DOM
      try {
        const productJson = document.querySelector('[data-product-json]');
        if (productJson && variant && variant.id) {
          const product = JSON.parse(productJson.textContent);
          const variantData = product.variants.find(v => String(v.id) === String(variant.id));
          if (variantData) {
            return variantData.available;
          }
        }
      } catch (e) {
        console.warn('Failed to parse product JSON:', e);
      }

      // Priority 3: Check variant input disabled state
      const variantInput = document.querySelector('input[name="id"].product-variant-id');
      if (variantInput && variant && String(variantInput.value) === String(variant.id)) {
        return !variantInput.disabled;
      }

      // Default to available if we can't determine
      return true;
    }

    toggleWrapper(show) {
      // Don't manage wrapper for bundle products
      const isBundleProduct = document.querySelector('[data-product-id]')?.dataset.bundleEnabled === 'true';
      if (isBundleProduct) return;

      if (this.wrapper) {
        this.wrapper.style.display = show ? 'block' : 'none';
      }
    }

    toggleAddToCart(show) {
      // Find add to cart button
      const addToCartButton = document.querySelector('.product-form__submit');
      if (addToCartButton) {
        addToCartButton.style.display = show ? '' : 'none';
      }

      // Find and toggle dynamic checkout wrapper
      const dynamicCheckout = document.querySelector('.shopify-payment-button');
      if (dynamicCheckout) {
        dynamicCheckout.style.display = show ? '' : 'none';
      }
    }

    openModal() {
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      this.trackEvent('bis_form_viewed', this.getEventProperties());

      this.trapFocus();

      const firstInput = this.form.querySelector('input[type="text"]');
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 100);
      }
    }

    closeModal() {
      const submitPending = Boolean(this.pendingSubmit);
      const wasFormFilled = this.form.querySelector('input[name="contact[email]"]')?.value.length > 0;
      // Track abandonment only if form was filled but NOT successfully submitted
      // successMessage.hidden = true means form was NOT successfully submitted
      if (wasFormFilled && this.successMessage.hidden && !submitPending) {
        this.trackEvent('bis_form_cancelled', this.getEventProperties({ form_filled: wasFormFilled }));
      }

      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.triggerButton.focus();
      // Keep the shopper's details while a submit is still pending (e.g. hCaptcha puzzle open)
      if (!submitPending) {
        this.resetForm();
      }
    }

    trapFocus() {
      const focusableElements = this.modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTabKey = (e) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };

      this.modal.addEventListener('keydown', handleTabKey);
    }

    debounceValidation(event) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.validateField(event.target);
      }, 300);
    }

    validateField(input) {
      const fieldWrapper = input.closest('.bis-form__field');
      if (!fieldWrapper) return true;

      const errorDiv = fieldWrapper.querySelector('.field__error');
      fieldWrapper.classList.remove('field--error');
      input.setAttribute('aria-invalid', 'false');

      if (errorDiv) {
        errorDiv.hidden = true;
        errorDiv.textContent = '';
      }

      if (!input.validity.valid) {
        fieldWrapper.classList.add('field--error');
        input.setAttribute('aria-invalid', 'true');
        this.showFieldError(input);
        return false;
      }

      this.syncValidationBanner();
      return true;
    }

    // Keep the top banner in step with field errors: once the field that caused a
    // validation message is fixed, show the next remaining field error or hide the banner.
    // Submission errors (not tied to a field) are left alone.
    syncValidationBanner() {
      if (this.errorMessage.hidden || this.errorSource !== 'validation') return;

      const remaining = this.form.querySelector('.field--error .field__error:not([hidden])');
      if (remaining && remaining.textContent) {
        this.showError(remaining.textContent, 'validation');
      } else if (!this.form.querySelector('.field--error')) {
        this.errorMessage.hidden = true;
        this.errorMessage.textContent = '';
        this.errorSource = null;
      }
    }

    showFieldError(input) {
      let errorMessage = '';

      if (input.validity.valueMissing) {
        if (input.type === 'email') {
          errorMessage = this.translations.emailRequired;
        } else if (input.name === 'contact[name]') {
          errorMessage = this.translations.nameRequired;
        }
      } else if (input.validity.typeMismatch && input.type === 'email') {
        errorMessage = this.translations.emailInvalid;
      } else if (input.validity.patternMismatch) {
        if (input.type === 'email') {
          errorMessage = this.translations.emailInvalid;
        } else if (input.type === 'tel') {
          errorMessage = this.translations.phoneInvalid;
        } else if (input.name === 'contact[name]') {
          errorMessage = this.translations.nameInvalid;
        }
      } else if (input.validity.tooShort) {
        if (input.type === 'tel') {
          errorMessage = this.translations.phoneTooShort;
        } else {
          // Locale uses "{{ min_length }}"; the JS fallback uses "{min_length}"
          errorMessage = this.translations.tooShort.replace(/\{\{\s*min_length\s*\}\}|\{min_length\}/, () => String(input.minLength));
        }
      }

      if (errorMessage) {
        const fieldWrapper = input.closest('.bis-form__field');
        const errorDiv = fieldWrapper?.querySelector('.field__error');
        if (errorDiv) {
          errorDiv.textContent = errorMessage;
          errorDiv.hidden = false;
        }
        this.showError(errorMessage, 'validation');
      }
    }

    handleSubmit(event) {
      event.preventDefault();

      if (this.isSubmitting) return;

      const inputs = this.form.querySelectorAll('input[required]');
      let isValid = true;

      inputs.forEach(input => {
        if (!this.validateField(input)) {
          isValid = false;
        }
      });

      if (!isValid) {
        this.showError(this.translations.validationFailed, 'validation');
        this.trackEvent('bis_form_error', this.getEventProperties({ error_type: 'validation' }));
        return;
      }

      this.isSubmitting = true;
      this.setSubmitting(true);
      this.hideMessages();

      // Get values for analytics
      const email = this.form.querySelector('input[name="contact[email]"]')?.value;
      const name = this.form.querySelector('input[name="contact[name]"]')?.value;
      const phone = this.form.querySelector('input[name="contact[phone]"]')?.value;

      // Track submission
      this.trackEvent('bis_form_submitted', this.getEventProperties({
        has_phone: phone?.length > 0,
        customer_email: email,
        customer_name: name
      }));

      // Shopify's hCaptcha wrapper replaces form.submit(): it runs the check (or a visual
      // puzzle), adds the h-captcha-response token to the form, then calls the submit
      // function the form had when it was bound, which is postContact() (installed in
      // connectedCallback). Stay on "Sending..." until Shopify's response has been read,
      // and give up after BIS_SUBMIT_TIMEOUT_MS.
      const pending = { posted: false, timeoutId: null };
      this.pendingSubmit = pending;
      pending.timeoutId = setTimeout(() => {
        if (this.pendingSubmit === pending) {
          this.finishSubmit('timeout');
        }
      }, BIS_SUBMIT_TIMEOUT_MS);

      this.form.submit();
    }

    /**
     * POST the contact form to Shopify with fetch() so the response can be read.
     * Shopify serves every storefront page with x-frame-options: DENY, so a hidden iframe
     * can never see the result. Only sends for a submit that passed handleSubmit()
     * validation, and only once per submit (the hCaptcha wrapper can call this more than
     * once, or on a submit event the theme rejected).
     */
    postContact() {
      const pending = this.pendingSubmit;
      if (!pending || pending.posted) return;
      pending.posted = true;

      // Shopify sends the shopper back to the referring page with ?contact_posted=true on
      // success. Drop a contact_posted left over from an earlier submit so only this
      // response can carry it.
      const referrer = new URL(window.location.href);
      referrer.searchParams.delete('contact_posted');
      referrer.hash = '';

      fetch(this.form.action, {
        method: 'POST',
        body: new URLSearchParams(new FormData(this.form)),
        credentials: 'same-origin',
        redirect: 'follow',
        referrer: referrer.href
      })
        .then((response) => {
          const result = this.getContactResult(response);
          // Only the final URL and headers are needed; skip downloading the page Shopify redirected to
          if (response.body) {
            response.body.cancel().catch(() => {});
          }
          return result;
        }, () => 'unreadable')
        .then((result) => {
          if (this.pendingSubmit === pending) {
            this.finishSubmit(result);
          }
        });
    }

    /**
     * Classify Shopify's response to the contact POST (after redirects).
     * @param {Response} response
     * @returns {String} 'posted', 'challenge', 'rejected', or 'unreadable'
     */
    getContactResult(response) {
      let url;
      try {
        url = new URL(response.url);
      } catch (e) {
        return 'unreadable';
      }
      // Shopify redirects an accepted contact POST to <page>?contact_posted=true; its own
      // 301s (/contact -> /pages/contact -> /pages/contact-us) keep the query string
      if (url.searchParams.get('contact_posted') === 'true') return 'posted';
      // Shopify's /challenge page, or a Cloudflare challenge (403 + cf-mitigated: challenge)
      if (url.pathname.includes('/challenge') || response.headers.get('cf-mitigated') === 'challenge') {
        return 'challenge';
      }
      return 'rejected';
    }

    finishSubmit(result) {
      const pending = this.pendingSubmit;
      if (!pending) return;
      this.pendingSubmit = null;
      clearTimeout(pending.timeoutId);

      if (result === 'challenge') {
        // Shopify wants a challenge completed: re-post in the main window so the customer can see it
        HTMLFormElement.prototype.submit.call(this.form);
        return;
      }

      this.isSubmitting = false;

      if (result === 'posted') {
        if (this.modal.hidden) {
          this.setSubmitting(false);
          this.resetForm();
          return;
        }
        // setSubmitting(false) after showSuccess() keeps the existing (live) button label;
        // successNotification is entity-decoded in loadTranslations() if it is ever shown
        this.showSuccess();
        this.setSubmitting(false);
        setTimeout(() => {
          if (!this.modal.hidden) {
            this.closeModal();
          }
        }, 3000);
        return;
      }

      this.setSubmitting(false);
      this.showError(this.translations.submissionFailed);
      this.trackEvent('bis_form_error', this.getEventProperties({ error_type: `contact_${result}` }));
    }

    setSubmitting(isSubmitting) {
      if (isSubmitting) {
        this.submitButton.disabled = true;
        this.submitButton.classList.add('loading');
        this.submitButton.textContent = this.translations.submitting;
      } else {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove('loading');
        this.submitButton.textContent = this.submitButton.dataset.originalText || this.translations.submitButtonDefault;
      }
    }

    showError(message, source = 'submission') {
      this.errorSource = source;
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
      this.successMessage.hidden = true;
    }

    showSuccess() {
      this.successMessage.hidden = false;
      this.errorMessage.hidden = true;
      this.submitButton.classList.add('bis-form__submit--success');
      this.submitButton.textContent = this.translations.successNotification;
    }

    hideMessages() {
      this.errorMessage.hidden = true;
      this.successMessage.hidden = true;
    }

    resetForm() {
      this.form.reset();
      this.hideMessages();
      this.submitButton.classList.remove('bis-form__submit--success');
      this.submitButton.textContent = this.submitButton.dataset.originalText || this.translations.submitButtonDefault;
      const fields = this.form.querySelectorAll('.bis-form__field');
      fields.forEach(field => {
        field.classList.remove('field--error');
        const input = field.querySelector('input');
        if (input) {
          input.setAttribute('aria-invalid', 'false');
        }
        const errorDiv = field.querySelector('.field__error');
        if (errorDiv) {
          errorDiv.hidden = true;
          errorDiv.textContent = '';
        }
      });
    }

    // Clean product title from the Liquid-rendered data attribute (product.title).
    // Fallback: the visible h1 only (the .product__title wrapper also holds a hidden
    // link copy of the title, which duplicated it). Whitespace is collapsed either way.
    getProductTitle() {
      const raw = this.dataset.productTitle
        || document.querySelector('.product__title h1')?.textContent
        || '';
      return raw.replace(/\s+/g, ' ').trim();
    }

    getEventProperties(additionalProps = {}) {
      const productId = this.form.querySelector('input[name="contact[product_id]"]')?.value;
      const variantId = this.variantIdInput?.value;
      const productSku = this.productSkuInput?.value;

      const productTitle = this.getProductTitle();

      const variantTitle = this.bodyInput?.dataset.variantTitle || '';

      // Get price from hidden form field (most reliable)
      let productPrice = this.form.querySelector('input[name="contact[product_price]"]')?.value;

      // Fallback: try to get from product JSON
      if (!productPrice) {
        try {
          const productJson = document.querySelector('[data-product-json]');
          if (productJson) {
            const product = JSON.parse(productJson.textContent);
            const variant = product.variants.find(v => String(v.id) === String(variantId));
            if (variant) {
              productPrice = String(variant.price);
            }
          }
        } catch (e) {
          console.warn('Failed to get price from product JSON:', e);
        }
      }

      const userLoggedIn = window.wrPageContext?.customerId !== null && window.wrPageContext?.customerId !== undefined;

      return {
        product_id: productId || null,
        variant_id: variantId || null,
        product_sku: productSku || null,
        product_title: productTitle || null,
        variant_title: variantTitle || null,
        product_price: productPrice || null,
        user_logged_in: userLoggedIn,
        currency: window.wrPageContext?.currency || 'USD',
        page_path: window.location.pathname,
        timestamp: new Date().toISOString(),
        ...additionalProps
      };
    }

    trackEvent(eventName, properties) {
      // Manual gtag removed — GA4 is now handled by the Google & YouTube app.
      // if (typeof gtag === 'function') {
      //   gtag('event', eventName, properties);
      // }

      if (typeof fbq === 'function') {
        fbq('trackCustom', eventName, properties);
      }

      if (window.Shopify?.theme?.role === 'development') {
        console.log('[BIS Analytics]', eventName, properties);
      }
    }

    connectedCallback() {
      // Only initialize once
      if (this.initialized) return;
      this.initialized = true;

      // Query for all elements now that DOM is ready
      this.triggerButton = this.querySelector('.bis-trigger-button');
      this.modal = this.querySelector('.bis-form__modal');
      this.overlay = this.querySelector('.bis-form__overlay');
      this.closeButton = this.querySelector('.bis-form__close');
      this.cancelButton = this.querySelector('.bis-form__cancel');
      this.form = this.querySelector('.bis-form__form');
      this.submitButton = this.querySelector('.bis-form__submit');
      this.errorMessage = this.querySelector('.bis-form__error-message');
      this.errorSource = null;
      this.successMessage = this.querySelector('.bis-form__success-message');
      this.variantIdInput = this.querySelector('.bis-variant-id');
      this.productSkuInput = this.querySelector('.bis-product-sku');
      this.bodyInput = this.querySelector('.bis-form__body');

      // Shopify's hCaptcha wrapper captures form.submit when it binds the form (on the first
      // focusin/change inside it, so only after the modal opens) and calls it once it has a
      // token. Install the fetch() sender first so the token-bearing post comes back to us.
      if (this.form) {
        this.form.submit = this.postContact.bind(this);
      }

      // Store reference to wrapper for visibility toggling
      this.wrapper = document.querySelector('[data-back-in-stock-wrapper]');

      // Load translations
      this.translations = this.loadTranslations();

      // Store original submit button text
      if (this.submitButton && !this.submitButton.dataset.originalText) {
        this.submitButton.dataset.originalText = this.submitButton.textContent;
      }

      // Cache focusable elements
      if (this.modal) {
        this.focusableElements = this.modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
      }

      // Setup event listeners
      this.setupEventListeners();
      this.setupVariantChangeListener();
    }

    disconnectedCallback() {
      if (this.handleEscape) {
        document.removeEventListener('keydown', this.handleEscape);
      }

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Unsubscribe from variant change events
      if (this.unsubscribeVariant) {
        this.unsubscribeVariant();
      }
    }
  }

  customElements.define('bis-form', BISForm);
}
